import { useCallback, useEffect, useRef, useState } from 'react';
import { usePaymentMethodSession } from '../../hooks/usePaymentMethodSession';
import type { PayienFormState } from '../PaymentMethodIframe';

/** Everything the card step needs, and the one signal that submits it. */
export interface PlanFlowCard {
  /** Payien's hosted form, once the session answered. */
  formUrl: string | null;
  sessionFailed: boolean;
  /** Bumped to ask the framed form to submit; the footer button owns it. */
  submitSignal: number;
  formState: PayienFormState;
  submit: () => void;
  onStateChange: (state: PayienFormState) => void;
  /** Drops the session, so the next visit mints a fresh one. */
  reset: () => void;
}

/**
 * The Payien session behind the card step.
 *
 * The session is minted the first time the step is actually shown and not
 * before: opening the modal to change a plan should not create a payment
 * session the buyer never asked for. One attempt per visit — the hook's own
 * in-flight guard cannot carry that, since a failed call leaves nothing
 * behind and the effect would retry forever.
 *
 * @param agencyId - Agency whose payment method is being replaced.
 * @param active - True while the card step is on screen.
 * @returns The form URL, the submit signal, and the form's reported state.
 */
export function usePlanFlowCard(agencyId: string, active: boolean): PlanFlowCard {
  const {
    formUrl, isError, createSession, reset: resetSession,
  } = usePaymentMethodSession(agencyId);
  const [submitSignal, setSubmitSignal] = useState(0);
  const [formState, setFormState] = useState<PayienFormState>('idle');
  const requested = useRef(false);

  useEffect(() => {
    if (!active || requested.current) return;
    requested.current = true;
    void createSession();
  }, [active, createSession]);

  const reset = useCallback(() => {
    requested.current = false;
    resetSession();
    setFormState('idle');
    setSubmitSignal(0);
  }, [resetSession]);

  const submit = useCallback(() => setSubmitSignal((signal) => signal + 1), []);

  return {
    formUrl,
    sessionFailed: isError,
    submitSignal,
    formState,
    submit,
    onStateChange: setFormState,
    reset,
  };
}

export default usePlanFlowCard;
